import type { ProcessRunDetailDto, RunNodeStatus } from '@api/processRuns';
import type { NodeExecutionDto, NodeExecutionStatus } from '@api/processes';
import type { ExecutionStep, RunOutcome, RunPhase } from '../editor/dock/ExecutionLog';
import type { NodeDisplayState } from '../editor/EditorContext';
import { latestStateByNode, persistedExecutionSteps } from '../editor/executionOrder';
import {
  dtoToReactFlow,
  type ProcessNode,
  type ProcessEdge,
  type NodeRunSnapshot,
} from '../editor/graphTypes';

const deriveRunOutcome = (status: string): RunOutcome | null => {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'failed') return 'failed';
  return null;
};

const RUN_NODE_TO_EXECUTION_STATUS: Record<RunNodeStatus, NodeExecutionStatus> = {
  pending: 'idle',
  provisioning: 'running',
  running: 'running',
  waiting_for_runner: 'running',
  waiting_for_user: 'running',
  completed: 'completed',
  failed: 'failed',
  skipped: 'failed',
};

const RUN_NODE_TO_DISPLAY: Record<RunNodeStatus, NodeDisplayState['status']> = {
  pending: 'idle',
  provisioning: 'running',
  running: 'running',
  waiting_for_runner: 'running',
  waiting_for_user: 'running',
  completed: 'completed',
  failed: 'failed',
  skipped: 'skipped',
};

export interface RunDetailGraph {
  readonly nodes: ProcessNode[];
  readonly edges: ProcessEdge[];
  readonly runStateMap: ReadonlyMap<string, NodeDisplayState>;
  readonly executionSteps: ExecutionStep[];
  readonly phase: RunPhase;
  readonly runOutcome: RunOutcome | null;
  readonly startedAtMs: number | null;
  readonly finishedAtMs: number | null;
}

/**
 * Convert a {@link ProcessRunDetailDto} into the shapes the graph canvas and
 * execution log need. Pure function — no hooks, no side effects.
 *
 * - Calls {@link dtoToReactFlow} on the snapshot graph
 * - Enriches each node's {@code data} with {@code latestExecution} and
 *   {@code latestArtifacts} from {@code nodeStates}
 * - Builds a {@code runStateMap} for the read-only EditorContext
 * - Builds {@code executionSteps} for the ExecutionLog sidebar
 */
export function buildRunDetailGraph(
  detail: ProcessRunDetailDto,
): RunDetailGraph {
  const { nodes, edges } = dtoToReactFlow(detail.graphSnapshot);

  const latestPerNode = latestStateByNode(detail.nodeStates);

  const runStateMap = new Map<string, NodeDisplayState>();

  for (const node of nodes) {
    const ns = latestPerNode.get(node.id);
    if (!ns) {
      node.data = { ...node.data, isRunnable: false };
      continue;
    }

    const execution: NodeExecutionDto = {
      status: RUN_NODE_TO_EXECUTION_STATUS[ns.status],
      executionTimeMs: ns.executionTimeMs,
      creditsSpent: ns.creditsSpent ?? null,
      startedAt: ns.startedAt,
      completedAt: ns.completedAt,
      errorMessage: ns.errorMessage,
      errorDetail: ns.errorDetail ?? null,
    };

    const snapshot: NodeRunSnapshot = {
      latestExecution: execution,
      latestArtifacts: ns.artifacts,
      isRunnable: false,
    };
    node.data = { ...node.data, ...snapshot };

    runStateMap.set(node.id, {
      status: RUN_NODE_TO_DISPLAY[ns.status],
      errorMessage: ns.errorMessage,
      errorDetail: ns.errorDetail ?? null,
      executionTimeMs: ns.executionTimeMs,
      creditsSpent: ns.creditsSpent ?? null,
      source: 'persisted',
    });
  }

  const executionSteps: ExecutionStep[] = persistedExecutionSteps(detail);

  const { run } = detail;
  const isTerminal =
    run.status === 'completed' ||
    run.status === 'failed' ||
    run.status === 'cancelled';
  const phase: RunPhase = isTerminal ? 'done' : 'running';
  const runOutcome = deriveRunOutcome(run.status);

  const startedAtSource = run.startedAt ?? run.createdAt;
  const startedAtMs = startedAtSource ? new Date(startedAtSource).getTime() : null;

  let finishedAtMs: number | null = null;
  if (run.completedAt) finishedAtMs = new Date(run.completedAt).getTime();
  else if (run.cancelledAt) finishedAtMs = new Date(run.cancelledAt).getTime();

  return {
    nodes,
    edges,
    runStateMap,
    executionSteps,
    phase,
    runOutcome,
    startedAtMs,
    finishedAtMs,
  };
}
